import day from "dayjs";
import { sift } from "radash";
import { useRef } from "react";
import { useLocalStorage } from "react-use";

import { useProductOpportunitiesState } from "../product-opportunities";
import { convertRange } from "../convert-range";
import { useActivityCounts } from "../../../data/use-reports";
import { useFunnelOpportunityCounts } from "data/use-reports.js";
import { useNetworkingCounts } from "../../../data/use-reports";
import { useOpportunityCountsBy } from "../../../data/use-reports";
import { useProductOpportunityCountsBy } from "../../../data/use-reports";
import { useUserActivityCounts } from "../../../data/use-reports";
import { useUserOpportunityCountsBy } from "../../../data/use-reports";

import { AccumulatingOpportunityValue } from "./accumulating-opportunity-value";
import { OpportunityFunnel } from "./opportunity-funnel";
import { OpportunityWinRate } from "./opportunity-win-rate";
import { Totals } from "./totals";
import { Activities } from "../activities";

import {
  useDashboardDateFilters,
} from "../use-dashboard-filters";
import { useOrganization } from "../../../data/use-organization";
import { useOpportunityStates } from "../../../data/use-opportunity-states";

export const IconicMetrics = () => {
  const organization = useOrganization();

  const date = useRef(day());

  const loading = [
    useActivityCounts(),
    useActivityCounts(
      convertRange([date.current.subtract(1, "month"), date.current])
    ),
    useFunnelOpportunityCounts(),
    useNetworkingCounts(
      convertRange([date.current.subtract(1, "month"), date.current])
    ),
    useOpportunityCountsBy("stateId"),
    useProductOpportunityCountsBy("stateId"),
    useUserActivityCounts(
      convertRange([date.current.subtract(1, "month"), date.current])
    ),
    useUserOpportunityCountsBy("stateId"),
  ];

  const dateRange = useDashboardDateFilters("ICONIC");

  const opportunityStates = useOpportunityStates();

  const potentialOpenStates = opportunityStates
    .all({ includeArchived: false })
    .filter(({ systemKey }) => systemKey !== "WON" && systemKey !== "LOST");

  const wonStateIds = opportunityStates
    .all()
    .filter(({ systemKey }) => systemKey === "WON")
    .map(({ _id }) => _id);

  const [currentStateIds, setCurrentStateIds] = useLocalStorage(
    `${organization.id}.ICONIC.DASHBOARD.CURRENT_STATE_IDS`,
    potentialOpenStates.map(({ _id }) => _id)
  );

  const [forecastStateIds, setForecastStateIds] = useLocalStorage(
    `${organization.id}.ICONIC.DASHBOARD.FORECAST_STATE_IDS`,
    potentialOpenStates.map(({ _id }) => _id)
  );

  const productOpportunitiesState = useProductOpportunitiesState(organization);

  if (sift(loading).length < loading.length) {
    return null;
  }

  return (
    <div
      style={{
        overflowY: "scroll",
      }}
    >
      <div className="mb-4 flex space-x-4">
        <div className="mr-4 w-1/2 space-y-4">
          <OpportunityFunnel
            hideWeighted
            title="Current Pipeline"
            {...{
              setSelectedStateIds: setForecastStateIds,
              selectedStateIds: forecastStateIds,
            }}
          />
        </div>
        <div className="mr-4 w-1/2 space-y-4">
          <OpportunityFunnel
            hideWeighted
            {...{
              setSelectedStateIds: setCurrentStateIds,
              selectedStateIds: currentStateIds,
            }}
            title="Opportunity Pipeline"
          />
        </div>
      </div>
      <div className="mb-4 flex space-x-4">
        <div className="mr-4 w-1/2 space-y-4">
          <AccumulatingOpportunityValue
            {...{
              selectedStateIds: wonStateIds,
            }}
            dateRange={dateRange}
          />
        </div>
        <div className="mr-4 w-1/2 space-y-4">
          <AccumulatingOpportunityValue
            title="Company Forecast"
            {...{
              selectedStateIds: forecastStateIds,
            }}
            dateRange={dateRange}
          />
        </div>
      </div>
      <div className="mb-4 flex space-x-4">
        <div className="mr-4 w-1/2 space-y-4">
          <OpportunityWinRate
            dateRange={dateRange}
            {...{
              selectedStateIds: wonStateIds,
            }}
            {...productOpportunitiesState}
          />
        </div>
        <div className="mr-4 w-1/2 space-y-4">
          <OpportunityWinRate
            dateRange={dateRange}
            title="Company Forecast"
            factorType="value"
            hideDetails
            showTotals
            {...{
              selectedStateIds: forecastStateIds,
            }}
            showFactoredValue
            {...productOpportunitiesState}
          />
        </div>
      </div>
      <div className="flex space-x-4">
        <div className="mr-4 w-1/2 space-y-4">
          <Activities dateRange={dateRange} />
        </div>
        <div className="mr-4 w-1/2 space-y-4">
          <Totals
            selectedStateIds={[...forecastStateIds, ...currentStateIds]}
          />
        </div>
      </div>
    </div>
  );
};
